// GLSL-Shader für den Jarvis-Iron-Man-Orb.
//
// Vertex: 4D Simplex-Noise displaced eine Icosaeder-Sphere, Time + Audio-Spektrum
// modulieren die Stärke. Normal wird approximiert über Sample-Nachbarn, damit
// Fresnel-Effekt sauber bleibt.
//
// Fragment: drei Schichten — Core-Glow (radialer Falloff), Fresnel-Rim,
// dynamische innere Strömung über zweite Noise-Pass. State bestimmt Farbe.

export const ORB_VERTEX_SHADER = /* glsl */ `
  uniform float uTime;
  uniform float uDistort;
  uniform float uAudio;
  uniform float uSpeed;
  varying vec3 vNormal;
  varying vec3 vPosition;
  varying float vDisplacement;

  // Ashima 4D Simplex Noise (gekürzt — Standard-Implementierung)
  vec4 mod289(vec4 x){return x - floor(x * (1.0/289.0)) * 289.0;}
  float mod289(float x){return x - floor(x * (1.0/289.0)) * 289.0;}
  vec4 permute(vec4 x){return mod289(((x*34.0)+1.0)*x);}
  float permute(float x){return mod289(((x*34.0)+1.0)*x);}
  vec4 taylorInvSqrt(vec4 r){return 1.79284291400159 - 0.85373472095314 * r;}
  float taylorInvSqrt(float r){return 1.79284291400159 - 0.85373472095314 * r;}
  vec4 grad4(float j, vec4 ip){
    const vec4 ones = vec4(1.0,1.0,1.0,-1.0);
    vec4 p,s;
    p.xyz = floor(fract(vec3(j) * ip.xyz)*7.0)*ip.z - 1.0;
    p.w = 1.5 - dot(abs(p.xyz), ones.xyz);
    s = vec4(lessThan(p, vec4(0.0)));
    p.xyz = p.xyz + (s.xyz*2.0 - 1.0)*s.www;
    return p;
  }
  float snoise(vec4 v){
    const vec2 C = vec2(0.138196601125010504,0.309016994374947451);
    vec4 i = floor(v + dot(v, C.yyyy));
    vec4 x0 = v - i + dot(i, C.xxxx);
    vec4 i0;
    vec3 isX = step(x0.yzw, x0.xxx);
    vec3 isYZ = step(x0.zww, x0.yyz);
    i0.x = isX.x + isX.y + isX.z;
    i0.yzw = 1.0 - isX;
    i0.y += isYZ.x + isYZ.y;
    i0.zw += 1.0 - isYZ.xy;
    i0.z += isYZ.z;
    i0.w += 1.0 - isYZ.z;
    vec4 i3 = clamp(i0, 0.0, 1.0);
    vec4 i2 = clamp(i0 - 1.0, 0.0, 1.0);
    vec4 i1 = clamp(i0 - 2.0, 0.0, 1.0);
    vec4 x1 = x0 - i1 + 1.0 * C.xxxx;
    vec4 x2 = x0 - i2 + 2.0 * C.xxxx;
    vec4 x3 = x0 - i3 + 3.0 * C.xxxx;
    vec4 x4 = x0 - 1.0 + 4.0 * C.xxxx;
    i = mod289(i);
    float j0 = permute(permute(permute(permute(i.w) + i.z) + i.y) + i.x);
    vec4 j1 = permute(permute(permute(permute(
        i.w + vec4(i1.w, i2.w, i3.w, 1.0))
      + i.z + vec4(i1.z, i2.z, i3.z, 1.0))
      + i.y + vec4(i1.y, i2.y, i3.y, 1.0))
      + i.x + vec4(i1.x, i2.x, i3.x, 1.0));
    vec4 ip = vec4(1.0/294.0, 1.0/49.0, 1.0/7.0, 0.0);
    vec4 p0 = grad4(j0, ip);
    vec4 p1 = grad4(j1.x, ip);
    vec4 p2 = grad4(j1.y, ip);
    vec4 p3 = grad4(j1.z, ip);
    vec4 p4 = grad4(j1.w, ip);
    vec4 norm = taylorInvSqrt(vec4(dot(p0,p0), dot(p1,p1), dot(p2,p2), dot(p3,p3)));
    p0 *= norm.x;
    p1 *= norm.y;
    p2 *= norm.z;
    p3 *= norm.w;
    p4 *= taylorInvSqrt(dot(p4,p4));
    vec3 m0 = max(0.6 - vec3(dot(x0,x0), dot(x1,x1), dot(x2,x2)), 0.0);
    vec2 m1 = max(0.6 - vec2(dot(x3,x3), dot(x4,x4)), 0.0);
    m0 = m0*m0;
    m1 = m1*m1;
    return 49.0 * (dot(m0*m0, vec3(dot(p0,x0), dot(p1,x1), dot(p2,x2)))
                 + dot(m1*m1, vec2(dot(p3,x3), dot(p4,x4))));
  }

  void main() {
    float t = uTime * uSpeed;
    float n = snoise(vec4(position * 1.4, t * 0.35)) * 0.5
            + snoise(vec4(position * 3.0, t * 0.6)) * 0.25;
    float audio = uAudio * 0.4;
    float displacement = n * (uDistort + audio);
    vDisplacement = displacement;
    vec3 newPosition = position + normal * displacement;
    vNormal = normalize(normalMatrix * normal);
    vPosition = (modelMatrix * vec4(newPosition, 1.0)).xyz;
    gl_Position = projectionMatrix * modelViewMatrix * vec4(newPosition, 1.0);
  }
`;

export const ORB_FRAGMENT_SHADER = /* glsl */ `
  uniform vec3 uColorCore;
  uniform vec3 uColorEdge;
  uniform float uTime;
  uniform float uIntensity;
  uniform float uAudio;
  varying vec3 vNormal;
  varying vec3 vPosition;
  varying float vDisplacement;

  void main() {
    vec3 viewDir = normalize(cameraPosition - vPosition);
    float vDotN = max(dot(viewDir, vNormal), 0.0);
    float fresnel = pow(1.0 - vDotN, 2.5);

    // Inner glow von der Mitte ausgehend (heller Kern)
    float innerGlow = pow(vDotN, 0.6) * 0.55;
    // Streifig-bewegter Schimmer über Displacement
    float ripple = smoothstep(-0.05, 0.25, vDisplacement) * 0.45;

    // Drei-Schicht-Color: Edge-Glow + Core-Mix + Ripple
    vec3 base = mix(uColorCore * 0.6, uColorEdge, fresnel);
    base += uColorCore * innerGlow;
    base += uColorEdge * ripple;

    // Audio-Hervorhebung am Rand
    float audioGlow = uAudio * fresnel * 0.8;
    base += uColorEdge * audioGlow;

    // Heller Hot-Spot mittig (Iron-Man-„Arc-Reactor"-Feel)
    float hotSpot = pow(vDotN, 8.0) * 0.5;
    base += vec3(1.0) * hotSpot * uIntensity * 0.4;

    float alpha = clamp(fresnel * 0.9 + innerGlow * 0.6 + 0.25, 0.0, 1.0);
    gl_FragColor = vec4(base * uIntensity, alpha);
  }
`;

export type OrbState =
  | "idle"
  | "wake"
  | "thinking"
  | "speaking"
  | "acting"
  | "confirming"
  | "blocked"
  | "done"
  | "error";

export interface StateProfile {
  colorCore: [number, number, number];
  colorEdge: [number, number, number];
  speed: number;
  distort: number;
  intensity: number;
  label: string;
}

// Farben in linear-RGB; THREE konvertiert vor dem Rendern.
export const STATE_PROFILES: Record<OrbState, StateProfile> = {
  idle: {
    colorCore: [0.05, 0.18, 0.55],
    colorEdge: [0.30, 0.55, 1.00],
    speed: 0.35,
    distort: 0.10,
    intensity: 0.9,
    label: "lauscht",
  },
  wake: {
    colorCore: [0.20, 0.55, 1.00],
    colorEdge: [0.55, 0.85, 1.00],
    speed: 1.6,
    distort: 0.20,
    intensity: 1.15,
    label: "erkannt",
  },
  thinking: {
    colorCore: [0.20, 0.06, 0.55],
    colorEdge: [0.65, 0.35, 1.00],
    speed: 0.9,
    distort: 0.18,
    intensity: 1.0,
    label: "denkt",
  },
  speaking: {
    colorCore: [0.04, 0.50, 0.62],
    colorEdge: [0.30, 0.95, 1.00],
    speed: 1.2,
    distort: 0.16,
    intensity: 1.05,
    label: "spricht",
  },
  acting: {
    colorCore: [0.80, 0.40, 0.05],
    colorEdge: [1.00, 0.85, 0.30],
    speed: 1.8,
    distort: 0.28,
    intensity: 1.1,
    label: "arbeitet",
  },
  confirming: {
    colorCore: [0.05, 0.40, 0.15],
    colorEdge: [0.35, 1.00, 0.55],
    speed: 0.7,
    distort: 0.12,
    intensity: 1.0,
    label: "wartet auf Go",
  },
  // SIMO-JARVIS-LIVING-AGENT-UI-RUNTIME-FINAL-1: "blocked" und "done" sind
  // eigene Owner-Zustaende (Live-Daten/Queue blockiert bzw. letzter Lauf sauber
  // abgeschlossen) und duerfen nicht auf "arbeitet"/"Fehler" gefaked werden.
  blocked: {
    colorCore: [0.45, 0.14, 0.03],
    colorEdge: [1.00, 0.55, 0.25],
    speed: 0.5,
    distort: 0.10,
    intensity: 0.95,
    label: "blockiert",
  },
  done: {
    colorCore: [0.03, 0.32, 0.20],
    colorEdge: [0.35, 0.95, 0.65],
    speed: 0.45,
    distort: 0.08,
    intensity: 0.95,
    label: "abgeschlossen",
  },
  error: {
    colorCore: [0.55, 0.05, 0.05],
    colorEdge: [1.00, 0.30, 0.30],
    speed: 2.5,
    distort: 0.35,
    intensity: 1.2,
    label: "Fehler",
  },
};
